import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies } from '@/mocks/movies';

export default function Detail() {
  const { id } = useParams<{ id: string }>();

  const movie = useMemo(() => movies.find((m) => m.id === id), [id]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id && (m.categorySlug === movie.categorySlug || m.tags.some((t) => movie.tags.includes(t))))
      .slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="container-site pt-24 pb-16 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-800 mb-4">影片不存在</h1>
          <Link to="/" className="btn-primary cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${movie.title} - 高清免费在线观看 | 6080影院`;
  const pageDesc = `${movie.title}，${movie.year}年${movie.region}${movie.category}，导演${movie.director}，主演${movie.actors.slice(0, 3).join('、')}等。${movie.description.slice(0, 80)}...在6080影院高清免费在线观看。`;

  return (
    <>
      <ScrollToTop />
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`${movie.title},${movie.category},高清在线观看,6080影院,${movie.region}${movie.category}`}
        canonicalUrl={`https://www.6080cinema.com/detail/${movie.id}`}
        ogType="article"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            dateCreated: `${movie.year}`,
            director: {
              '@type': 'Person',
              name: movie.director,
            },
            actor: movie.actors.map((a) => ({
              '@type': 'Person',
              name: a,
            })),
            genre: movie.tags,
            countryOfOrigin: {
              '@type': 'Country',
              name: movie.region,
            },
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: movie.rating,
              bestRating: '10',
              worstRating: '0',
            },
          }),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="pt-20 pb-0">
            <div className="container-site">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: movie.category, href: `/category/${movie.categorySlug}` },
                  { label: movie.title },
                ]}
              />
            </div>
          </div>

          <div className="py-8 md:py-12">
            <div className="container-site">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-shrink-0 w-full sm:w-[280px] lg:w-[320px] mx-auto lg:mx-0">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                    <img
                      src={movie.coverImage}
                      alt={movie.title}
                      title={`${movie.title} - ${movie.category} | 6080影院`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                    {movie.title}
                  </h1>

                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-accent-500/10 border border-accent-500/20">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                      <span className="text-accent-500 font-bold text-sm">{movie.rating}</span>
                    </span>
                    <span className="text-sm text-foreground-500">{movie.year}</span>
                    <span className="text-sm text-foreground-500">{movie.region}</span>
                    <span className="text-sm text-foreground-500">{movie.category}</span>
                    <span className="text-sm text-foreground-500">{movie.duration}</span>
                    {movie.type === 'tv' && (
                      <span className="px-2 py-0.5 text-xs bg-secondary-500/20 text-secondary-400 rounded">
                        电视剧 · {movie.episodes}集
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 border border-secondary-200"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-foreground-800 mb-2">剧情简介</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {movie.description}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-800 mb-1">导演</h3>
                      <p className="text-sm text-foreground-500">{movie.director}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-800 mb-1">主演</h3>
                      <p className="text-sm text-foreground-500">{movie.actors.join(' / ')}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Link
                      to={movie.categorySlug ? `/category/${movie.categorySlug}` : '/'}
                      className="btn-primary cursor-pointer"
                    >
                      <i className="ri-play-fill mr-2"></i>
                      立即播放
                    </Link>
                    <button className="inline-flex items-center justify-center px-4 py-3 rounded-md font-medium text-sm whitespace-nowrap cursor-pointer transition-all duration-300 border border-background-300 text-foreground-600 hover:bg-background-100">
                      <i className="ri-heart-line mr-2"></i>
                      收藏
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {relatedMovies.length > 0 && (
            <section className="py-10 md:py-14 bg-background-100/50">
              <div className="container-site">
                <div className="mb-6">
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                    猜你喜欢
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">根据该影片为您推荐</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}